//* 组件库
import type { UiTable2 } from "various-ui";
//* 组件属性
import type { ModuleData, ModuleInfo } from "../index";
import { useOptions } from "./options";

export const useComposable = () => {
    //* 插件
    const route = useRoute();

    //* 配置项
    const options = useOptions();

    //* 响应式变量
    const refs = {
        //* 页面数据
        data: ref<ModuleInfo[]>([]),
        exist: ref<boolean>(true),
        loading: ref<boolean>(true),

        //* 配置
        filters: ref<any>({}),
        regulatory_details: ref<string[]>([]),
        regulatory_detail: ref<boolean>(false),
        regulation_modes: ref<string[]>([]),
        regulation_mode: ref<boolean>(false),
        mechanism: ref<string>(""),
        species: ref<string>(""),
        updown: ref<string>(""),
        tissue: ref<string>(""),
        count: ref<number>(0),
        sort: ref<{ key: string; value: string }>({ key: "", value: "" }),
        page: ref<number>(1),
        mode: ref<string>(""),
        cell: ref<string>(""),
    };

    //* 节点
    const nodes = {
        table: ref<InstanceType<typeof UiTable2>>(),
        container: ref<HTMLDivElement>(),
    };

    //* 函数列表
    const methods = {
        //* 排序函数
        sort: (result: { key: string; value: string }) => {
            //* 数据更新
            refs.sort.value = result;
            //* 数据获取
            methods.acquire(1);
        },

        //* 筛选重置函数
        reset: () => {
            //* 数据更新
            refs.cell.value = "";
            refs.mode.value = "";
            refs.tissue.value = "";
            refs.updown.value = "";
            refs.species.value = "";
            refs.mechanism.value = "";
            refs.regulation_mode.value = false;
            refs.regulation_modes.value = [];
            refs.regulatory_detail.value = false;
            refs.regulatory_details.value = [];
            //* 表格排序重置
            nodes.table.value?.sort();
            //* 数据获取
            methods.acquire(1);
        },

        //* 渲染函数
        render: (data: ModuleData) => {
            //* 数据初始化
            refs.filters.value = data.filters;
            refs.data.value = data.data || [];
            refs.page.value = data.page || 1;
            refs.count.value = data.count || 0;
            refs.loading.value = false;
        },

        //* 隐藏函数
        hidden: () => {
            refs.exist.value = false;
            refs.loading.value = false;
        },

        //* 数据获取
        acquire: (number?: number) => {
            //* 数据更新
            refs.loading.value = true;
            refs.page.value = number || refs.page.value;

            //* 排序初始化
            let sort = refs.sort.value.value == "desc" ? "-" : "";
            if (refs.sort.value.key == "effect_name") {
                sort += "regulator_protein";
            } else if (refs.sort.value.key == "target_name") {
                sort += "target_protein";
            } else if (refs.sort.value.key == "aminos") {
                sort += "residue_count";
            } else if (refs.sort.value.key == "references") {
                sort += "paper_count";
            } else if (refs.sort.value.key == "score") {
                sort += "score";
            }

            //* 筛选项初始化
            const regulation_modes = refs.regulation_modes.value.map((value) => ({
                field: "effect",
                value: value,
            }));
            const regulatory_details = refs.regulatory_details.value.map((value) => ({
                field: "effect_detail",
                value: value,
            }));

            const filters = [
                {
                    field: "mode_action",
                    value: refs.mode.value,
                },
                {
                    field: "regulator_protein",
                    value: refs.updown.value,
                },
                {
                    field: "mechanism",
                    value: refs.mechanism.value,
                },
                {
                    field: "species",
                    value: refs.species.value,
                },
                {
                    field: "tissues",
                    value: refs.tissue.value,
                },
                {
                    field: "cell_lines",
                    value: refs.cell.value,
                },
                ...regulation_modes,
                ...regulatory_details,
            ].filter((value) => value.value.length > 0);

            //* 接口请求
            api.frontend(`/api/gene/protein-interaction`, {
                finally: () => (refs.loading.value = false),
                method: "POST",
                data: {
                    id: route.params.id,
                    mode: "gene",
                    page: refs.page.value,
                    sort: sort ? [sort] : [],
                    filter_options: filters,
                },
                success: (response) => {
                    refs.data.value = response?.data || [];
                    refs.count.value = response?.count || 0;
                    //* 位置移动
                    if (nodes.container.value && scrollTo) {
                        scrollTo({ top: nodes.container.value.offsetTop - 200, behavior: "smooth" });
                    }
                },
            });
        },

        //* 表格子菜单开关函数
        children: (mode: string, data: ModuleInfo) => {
            //* 子菜单不同类型
            if (data.mode && data.mode != mode) {
                //* 子菜单收起
                nodes.table.value?.children(data.index);
            }

            //* 子菜单类型更新
            data.mode = mode;

            //* 子菜单状态更新
            nodes.table.value?.children(data.index, (result: boolean) => {
                if (!result) data.mode = "";
            });
        },

        //* 调控方式选择回调
        regulation_modes: () => {
            //* 数据更新
            refs.regulation_mode.value = refs.regulation_modes.value.length == refs.filters.value.regulation_modes.length;
            //* 数据获取
            methods.acquire(1);
        },

        //* 调控方式全选回调
        regulation_mode: () => {
            //* 数据更新
            if (refs.regulation_mode.value) {
                refs.regulation_modes.value = [];
                refs.filters.value.regulation_modes.options.forEach((value: any) => {
                    refs.regulation_modes.value.push(value.value);
                });
            } else {
                refs.regulation_modes.value = [];
            }

            //* 数据获取
            methods.acquire(1);
        },

        //* 调控方式选择回调
        regulatory_details: () => {
            //* 数据更新
            refs.regulatory_detail.value = refs.regulatory_details.value.length == refs.filters.value.regulatory_details.length;
            //* 数据获取
            methods.acquire(1);
        },

        //* 调控方式全选回调
        regulatory_detail: () => {
            //* 数据更新
            if (refs.regulatory_detail.value) {
                refs.regulatory_details.value = [];
                refs.filters.value.regulatory_details.options.forEach((value: any) => {
                    refs.regulatory_details.value.push(value.value);
                });
            } else {
                refs.regulatory_details.value = [];
            }

            //* 数据获取
            methods.acquire(1);
        },
    };

    return { refs, nodes, methods, options };
};
